/**
 * Home Feed Auto-Peek bus — emits a discreet "peek" pulse on the FAB
 * whenever a new discoverable item appears (Fan DNA, Prestige, World
 * Status, Featured Fan, viral capsule). Subscribers (RetentionFab) light
 * the FAB briefly. Pure in-memory pub/sub, no polling, no realtime.
 */
import { trackEvent } from "@/lib/audit";

export type PeekReason =
  | "fan_dna"
  | "prestige"
  | "world_status"
  | "featured_fan"
  | "viral_capsule";

type Listener = (reason: PeekReason) => void;
const listeners = new Set<Listener>();
let lastEmittedAt: Partial<Record<PeekReason, number>> = {};
const MIN_GAP_MS: Record<PeekReason, number> = {
  fan_dna: 60_000,
  prestige: 45_000,
  world_status: 60_000,
  featured_fan: 90_000,
  viral_capsule: 45_000,
};

export function subscribePeek(cb: Listener): () => void {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

export function emitPeek(reason: PeekReason): void {
  const now = Date.now();
  const last = lastEmittedAt[reason] ?? 0;
  if (now - last < MIN_GAP_MS[reason]) return;
  lastEmittedAt[reason] = now;
  void trackEvent("homefeed_auto_peek_triggered", { reason });
  for (const l of Array.from(listeners)) {
    try { l(reason); } catch { /* isolated */ }
  }
}

export function _resetPeek(): void {
  listeners.clear();
  lastEmittedAt = {};
}